"use client"

import React, { useState, useEffect } from "react";
import Image from 'next/image';
import assetServices from "../../utils/services/asset";
import { useLanguages } from '@/contexts/LanguageContext';
import { useIdPath } from '@/contexts/IdPathProvider';
import ArticleManagement from "./articleManagement";
import LayoutDefault from "../../layouts/LayoutDefault";
import { useToast } from "@/templates/Admin/components/Toast";
import useDebounce from "@/hooks/useDebounce";
import { getSession } from 'next-auth/react';
import userService from "../../utils/services/user";
import permissionService from '@/services/permissionService';
import { getProjectIdPath } from '@/utils/projectUtils';


interface BlogPost {
    uuid?: string;
    title: Record<string, string>;
    subtitle?: Record<string, string>;
    content: Record<string, string>;
    featuredImage?: string;
    type?: string;
    categories: string[];
    approve?: number; // 0 = chưa duyệt, 1 = đã duyệt
    viewCount?: number;
    creator?: { id: number; name: string } | null;
    updater?: { id: number; name: string } | null;
    updatedAt?: Date;
    createdAt?: any;
}

interface CategoryData {
    uuid: string;
    description: string | null;
    parent_uuid: string | null;
    is_show: number;
    index: number | null;
    view_count: number;
    display_mode: number;
    created_by: number | null;
    created_at: string | null;
    updated_by: number | null;
    updated_at: string;
    category_title: string;
    translations?: any;
}

interface CategoryAndAssetResponse {
    asset_type: {
        id: number;
        name: string;
    };
    parent_category: CategoryData | null;
    requested_level: number;
    parent_category_uuid: string;
    item_type: 'categories' | 'assets';
    total_items: number;
    categories: CategoryData[];
    assets: any[];
}

interface BlogListManagerProps {
    onClose: () => void;
    isSidebarOpen: boolean;
    mode?: 'create' | 'update';
    initialData?: BlogPost;
}

export default function BlogListManager({ onClose, isSidebarOpen }: BlogListManagerProps) {
    const { languages, currentLanguage } = useLanguages();
    const { idPath } = useIdPath();
    const { showSuccess, showError } = useToast();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [filteredCategories, setFilteredCategories] = useState<CategoryData[]>([]);
    const [showEditor, setShowEditor] = useState(false);
    const [editingPost, setEditingPost] = useState<BlogPost | undefined>(undefined);

    // Category management state
    const [blogAssetTypeId, setBlogAssetTypeId] = useState<number | null>(null);
    const [categoryResponse, setCategoryResponse] = useState<CategoryAndAssetResponse | null>(null);
    const [categoryHistory, setCategoryHistory] = useState<{ category: CategoryData, level: number }[]>([]);
    const [selectedCategoryUuid, setSelectedCategoryUuid] = useState<string>('');
    const [createCategoryVisible, setCreateCategoryVisible] = useState(false);
    const [createCategoryLoading, setCreateCategoryLoading] = useState(false);
    const createCategoryFormRef = React.useRef<HTMLFormElement>(null);
    // Delete confirmation modal state
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);
    const [deleteCategoryModalVisible, setDeleteCategoryModalVisible] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<CategoryData | null>(null);
    const [editingCategory, setEditingCategory] = useState<CategoryData | null>(null);

    // Admin role check
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    // Debounce search input
    const searchQuery = useDebounce(searchInput, 500);

    // Load user role to check if admin
    useEffect(() => {
        loadUserRole();
    }, []);

    const loadUserRole = async () => {
        try {
            const session = await getSession();

            if (session?.userData?.userId || session?.userData?.username || session?.user?.email) {
                const userId = session.userData?.userId || session.userData?.username || session.userData?.id;

                let user = null;

                // Try to get user by ID first
                if (userId && !isNaN(Number(userId))) {
                    try {
                        user = await userService.getUserById(Number(userId));
                    } catch (error) {
                        console.log('User not found by ID:', error);
                    }
                }

                // If no user found, try search
                if (!user) {
                    const searchTerm = userId || (session?.user?.email ? session.user.email.split('@')[0] : '');
                    if (searchTerm) {
                        try {
                            const usersResponse = await userService.getAllUsers({
                                search: searchTerm,
                                limit: 10
                            });

                            if (usersResponse.success && usersResponse.data.length > 0) {
                                user = usersResponse.data.find((u: any) =>
                                    u.username === searchTerm ||
                                    u.email === session?.user?.email ||
                                    u.username === session?.userData?.username
                                ) || usersResponse.data[0];
                            }
                        } catch (error) {
                            console.log('User not found by search:', error);
                        }
                    }
                }

                if (user) {
                    // Check role directly from user object
                    const userRole = user.role?.toLowerCase();
                    if (userRole === 'admin') {
                        setIsAdmin(true);
                        return;
                    }

                    // Check permission group
                    const groupName = await loadPermissionGroupName(user.id || user.user_id);
                    if (groupName?.toLowerCase() === 'admin') {
                        setIsAdmin(true);
                        return;
                    }
                }

                // Fallback: check session data
                const sessionRole = session.userData?.role?.toLowerCase();
                const sessionGroupName = session.userData?.groupName?.toLowerCase();
                if (sessionRole === 'admin' || sessionGroupName === 'admin') {
                    setIsAdmin(true);
                }
            }
        } catch (error) {
            console.error('Error loading user role:', error);
            setIsAdmin(false);
        }
    };

    // Load Permission Group name by user ID
    const loadPermissionGroupName = async (userId: number): Promise<string> => {
        try {
            const idPathForPermission = getProjectIdPath();
            const response = await permissionService.getAllPermissionGroups({
                id_path: idPathForPermission,
                page: 1,
                limit: 100
            });

            if (response.success && response.data) {
                for (const group of response.data) {
                    const groupWithUsers = group as { users?: { id: number; user_id?: number }[] } & typeof group;
                    if (groupWithUsers.users && groupWithUsers.users.some((user) => user.id === userId || user.user_id === userId)) {
                        return group.name;
                    }
                }
            }
            return 'User';
        } catch (error) {
            console.error('Error loading permission group name:', error);
            return 'User';
        }
    };

    useEffect(() => {
        if (blogAssetTypeId) {
            setCategoryHistory([]);
            setSelectedCategoryUuid('');
            loadCategoryTree(blogAssetTypeId, 0, '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blogAssetTypeId]);

    const mapCategoryAssetsToBlogPosts = (assets: any[]): BlogPost[] => {
        return assets.map((asset) => {
            const attributes = asset.attributes || {};
            const title: Record<string, string> = {};
            const subtitle: Record<string, string> = {};
            const content: Record<string, string> = {};
            const excerpt: Record<string, string> = {};
            let featuredImage: string | undefined;
            let postType: string | undefined;

            Object.entries(attributes).forEach(([key, value]: [string, any]) => {
                if (!value || typeof value.values !== 'string') return;
                if (key === 'thumbnail') {
                    featuredImage = value.values;
                    return;
                }
                if (key === 'type') {
                    postType = value.values;
                    return;
                }

                const [type, ...langSegments] = key.split('_');
                const langCode = langSegments.join('_').toLowerCase() || 'vn';

                if (type === 'title') {
                    title[langCode] = value.values;
                } else if (type === 'subtitle') {
                    subtitle[langCode] = value.values;
                } else if (type === 'description') {
                    content[langCode] = value.values;
                } else if (type === 'excerpt') {
                    excerpt[langCode] = value.values;
                }
            });

            if (!title['vn'] && asset.asset_title) {
                title['vn'] = asset.asset_title;
            }

            return {
                uuid: asset.uuid,
                title,
                subtitle,
                content,
                featuredImage,
                type: postType,
                categories: asset.category_uuid ? [asset.category_uuid] : [],
                approve: asset.approve ?? 0,
                createdAt: asset.created_at ? new Date(asset.created_at) : undefined,
                viewCount: asset.view_count ?? 0,
                creator: asset.creator,
                updater: asset.updater,
                updatedAt: asset.updated_at ? new Date(asset.updated_at) : undefined,
            };
        });
    };

    useEffect(() => {
        if (searchQuery) {
            // When searching, filter all available posts (current category assets)
            const allAvailablePosts: BlogPost[] = [];
            if (categoryResponse?.assets?.length) {
                const categoryPosts = mapCategoryAssetsToBlogPosts(categoryResponse.assets);
                allAvailablePosts.push(...categoryPosts);
            }
            applyFiltersToAssets(allAvailablePosts);
            applyFiltersToCategories(categoryResponse?.categories || []);
        } else {
            let baseAssets: BlogPost[] = [];
            if (categoryResponse?.assets?.length) {
                baseAssets = mapCategoryAssetsToBlogPosts(categoryResponse.assets);
            }

            applyFiltersToAssets(baseAssets);
            applyFiltersToCategories(categoryResponse?.categories || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryResponse, searchQuery, selectedCategoryUuid]);

    useEffect(() => {
        if (!idPath) return;
        const initAssetType = async () => {
            try {
                const response = await assetServices.getAllAssetTypes({ id_path: idPath || '' }) as any;
                // console.log('Asset types response:', response);
                const assetTypes = response?.data || [];
                const blogType = assetTypes.find((type: any) => type.name?.toLowerCase() === 'blog');
                if (blogType) {
                    setBlogAssetTypeId(blogType.id);
                } else if (assetTypes.length > 0) {
                    setBlogAssetTypeId(assetTypes[0].id);
                }
            } catch (error) {
                console.error('Error loading blog asset type:', error);
            }
        };

        initAssetType();
    }, [idPath]);

    // Load category data into form when editing
    useEffect(() => {
        if (editingCategory && createCategoryFormRef.current) {
            const form = createCategoryFormRef.current;

            // Set description
            const descriptionInput = form.querySelector('textarea[name="description"]') as HTMLTextAreaElement;
            if (descriptionInput) {
                descriptionInput.value = editingCategory.description || '';
            }

            // Set category titles for each language
            languages.forEach((lang) => {
                const langCode = lang.id;
                const inputName = `category_title_${langCode}`;
                const input = form.querySelector(`input[name="${inputName}"]`) as HTMLInputElement;

                if (input) {
                    // Get title from translations or fallback to category_title for Vietnamese
                    if (langCode === 'vn') {
                        input.value = editingCategory.category_title || '';
                    } else {
                        const translation = editingCategory.translations?.[langCode];
                        input.value = translation?.title || '';
                    }
                }
            });
        }
    }, [editingCategory, languages]);

    const fetchCategoryAndAssets = async (
        assetTypeId: number,
        level: number = 0,
        parentUuid: string = ''
    ): Promise<CategoryAndAssetResponse> => {
        const response = await assetServices.getCategoryAndAssetWithType({
            id_path: idPath || '',
            language_id: 'vn',
            level,
            parent_category_uuid: parentUuid,
            asset_type: assetTypeId
        }) as any;

        if (response?.success && response.data) {
            return response.data as CategoryAndAssetResponse;
        }

        throw new Error('Không thể tải cây danh mục/asset');
    };

    const loadCategoryTree = async (
        assetTypeId: number,
        level: number = 0,
        parentUuid: string = ''
    ) => {
        try {
            const data = await fetchCategoryAndAssets(assetTypeId, level, parentUuid);
            setCategoryResponse(data);
            setSelectedCategoryUuid(parentUuid);
        } catch (error) {
            console.error('Error loading category tree:', error);
            setCategoryResponse(null);
        }
    };

    const handleCategorySelect = async (category: CategoryData) => {
        if (!blogAssetTypeId) return;

        // Check if this category is already in history to prevent duplicates
        setCategoryHistory(prev => {
            // If the last item in history is the same category, don't add it again
            if (prev.length > 0 && prev[prev.length - 1].category.uuid === category.uuid) {
                return prev;
            }
            return [...prev, {
                category: category,
                level: (categoryResponse?.requested_level || 0) + 1
            }];
        });

        // Load next level
        const nextLevel = (categoryResponse?.requested_level || 0) + 1;
        setSelectedCategoryUuid(category.uuid);
        await loadCategoryTree(blogAssetTypeId, nextLevel, category.uuid);
    };

    const handleCategoryBack = () => {
        if (!blogAssetTypeId) return;
        if (categoryHistory.length === 0) {
            // Already at root
            return;
        }

        const newHistory = [...categoryHistory];
        newHistory.pop(); // Remove current level
        setCategoryHistory(newHistory);

        if (newHistory.length === 0) {
            // Back to root
            setSelectedCategoryUuid('');
            loadCategoryTree(blogAssetTypeId, 0, '');
        } else {
            // Back to previous category
            const previousItem = newHistory[newHistory.length - 1];
            setSelectedCategoryUuid(previousItem.category.uuid);
            loadCategoryTree(blogAssetTypeId, previousItem.level, previousItem.category.uuid);
        }
    };

    const applyFiltersToAssets = (assets: BlogPost[]) => {
        let filteredPosts = [...assets];

        if (searchQuery) {
            filteredPosts = filteredPosts.filter((post) => {
                const title = post.title?.[currentLanguage?.code || 'vn'] || post.title?.vn || '';
                const subtitle = post.subtitle?.[currentLanguage?.code || 'vn'] || post.subtitle?.vn || '';
                const content = post.content?.[currentLanguage?.code || 'vn'] || post.content?.vn || '';
                const searchLower = searchQuery.toLowerCase();
                return title.toLowerCase().includes(searchLower) ||
                    subtitle.toLowerCase().includes(searchLower) ||
                    content.toLowerCase().includes(searchLower);
            });
        }
        setPosts(filteredPosts);
    };

    const applyFiltersToCategories = (categories: CategoryData[]) => {
        if (!searchQuery) {
            setFilteredCategories([]);
            return;
        }

        const filtered = categories.filter((category) => {
            const title = category.category_title || '';
            const description = category.description || '';
            const searchLower = searchQuery.toLowerCase();
            return title.toLowerCase().includes(searchLower) || description.toLowerCase().includes(searchLower);
        });
        setFilteredCategories(filtered);
    };

    const refreshPosts = () => {
        if (selectedCategoryUuid && blogAssetTypeId) {
            const level = categoryResponse?.requested_level || 0;
            loadCategoryTree(blogAssetTypeId, level, selectedCategoryUuid);
        } else {
            loadCategoryTree(blogAssetTypeId, 0, '');
        }
    };

    const handleDeletePost = (post: BlogPost) => {
        setPostToDelete(post);
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (!postToDelete?.uuid) return;

        try {
            await assetServices.deleteAsset({ id_path: idPath, asset_uuid: postToDelete.uuid });
            showSuccess('Xóa bài viết thành công!', 'Thành công');
            setDeleteModalVisible(false);
            setPostToDelete(null);
            refreshPosts();
        } catch (error) {
            console.error('Error deleting post:', error);
            showError('Xóa bài viết thất bại!', 'Lỗi');
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setPostToDelete(null);
    };

    const handleDeleteCategory = (category: CategoryData) => {
        setCategoryToDelete(category);
        setDeleteCategoryModalVisible(true);
    };

    const handleConfirmDeleteCategory = async () => {
        if (!categoryToDelete?.uuid) return;

        try {
            await assetServices.deleteCategory({
                id_path: idPath,
                category_uuid: categoryToDelete.uuid
            });
            showSuccess('Xóa category thành công!', 'Thành công');
            setDeleteCategoryModalVisible(false);
            setCategoryToDelete(null);
            // Refresh category tree
            if (blogAssetTypeId) {
                loadCategoryTree(blogAssetTypeId, categoryResponse?.requested_level || 0, categoryResponse?.parent_category_uuid || '');
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            showError('Xóa category thất bại!', 'Lỗi');
        }
    };

    const handleCancelDeleteCategory = () => {
        setDeleteCategoryModalVisible(false);
        setCategoryToDelete(null);
    };

    const handleEditPost = (post: BlogPost) => {
        setEditingPost(post);
        setShowEditor(true);
    };

    // Handle approve post (toggle)
    const handleApprovePost = async (post: BlogPost) => {
        if (!post?.uuid) return;

        const newApprovalStatus = post.approve === 1 ? 0 : 1;

        try {
            // Call API to toggle approval status
            await assetServices.updateAsset({
                id_path: idPath,
                asset_uuid: post.uuid,
                data: { approve: newApprovalStatus }
            });

            // Update local state after successful API call
            setPosts(prevPosts =>
                prevPosts.map(p =>
                    p.uuid === post.uuid
                        ? { ...p, approve: newApprovalStatus }
                        : p
                )
            );

            showSuccess(
                newApprovalStatus === 1
                    ? 'Đã phê duyệt bài viết!'
                    : 'Đã hủy phê duyệt bài viết!',
                'Thành công'
            );
        } catch (error) {
            console.error('Error toggling post approval:', error);
            showError('Cập nhật trạng thái thất bại!', 'Lỗi');
        }
    };

    const handleCreateNew = () => {
        setEditingPost(undefined);
        setShowEditor(true);
    };

    const handleEditorClose = () => {
        setShowEditor(false);
        setEditingPost(undefined);
        refreshPosts();
    };

    // Handle create category
    const handleCreateCategory = () => {
        setCreateCategoryVisible(true);
    };

    // Handle save category
    const handleSaveCategory = async () => {
        if (!createCategoryFormRef.current) {
            return;
        }

        try {
            setCreateCategoryLoading(true);

            // Collect form data
            const formData = new FormData(createCategoryFormRef.current);
            const description = formData.get('description')?.toString() || '';

            // Collect category titles for each language
            const translations: Record<string, { title: string }> = {};
            let categoryTitleVn = '';

            languages.forEach((lang) => {
                const langCode = lang.id;
                const inputName = `category_title_${langCode}`;
                const titleValue = formData.get(inputName)?.toString() || '';

                if (titleValue.trim()) {
                    translations[langCode] = { title: titleValue.trim() };
                }

                // Store Vietnamese title for category_title field
                if (langCode === 'vn') {
                    categoryTitleVn = titleValue.trim();
                }
            });

            if (!categoryTitleVn) {
                showError('Vui lòng nhập tên category bằng tiếng Việt!', 'Lỗi');
                return;
            }

            // Prepare request body for blog category
            const body = {
                category_title: categoryTitleVn,
                description: description || null,
                is_show: 1,
                index: null,
                display_mode: 2,
                translations,
            };

            let response;
            if (editingCategory) {
                // Update existing category
                response = await assetServices.updateCategory({
                    id_path: idPath || '',
                    category_uuid: editingCategory.uuid,
                    data: body
                }) as any;
            } else {
                // Create new category
                response = await assetServices.createCategory({
                    id_path: idPath || '',
                    data: {
                        ...body,
                        parent_uuid: categoryResponse?.parent_category?.uuid || null
                    }
                }) as any;
            }

            if (response?.success || response?.message || response?.category_uuid) {
                showSuccess(
                    editingCategory ? 'Cập nhật category thành công!' : 'Tạo category thành công!',
                    'Thành công'
                );
                handleCloseCategoryForm();
                // Refresh category tree
                if (blogAssetTypeId) {
                    loadCategoryTree(
                        blogAssetTypeId,
                        categoryResponse?.requested_level || 0,
                        categoryResponse?.parent_category_uuid || ''
                    );
                }
            } else {
                console.error('Save failed:', response);
                showError('Có lỗi xảy ra khi lưu category. Vui lòng thử lại!', 'Lỗi');
            }
        } catch (err) {
            console.error('Error saving category:', err);
            showError('Có lỗi xảy ra khi lưu category. Vui lòng thử lại!', 'Lỗi');
        } finally {
            setCreateCategoryLoading(false);
        }
    };

    // Close create category form
    const handleCloseCategoryForm = () => {
        setCreateCategoryVisible(false);
        setEditingCategory(null);
        if (createCategoryFormRef.current) {
            createCategoryFormRef.current.reset();
        }
    };

    const formatDate = (date?: Date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (showEditor) {
        return (
            <ArticleManagement
                parentCategory={selectedCategoryUuid}
                onClose={handleEditorClose}
                isSidebarOpen={true}
                mode={editingPost ? 'update' : 'create'}
                initialData={editingPost}
            />
        );
    }

    return (
        <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen}>
            <div className="flex flex-col h-full bg-white">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-2xl font-bold text-gray-900">Quản lý bài viết</h1>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleCreateCategory}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center space-x-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                                <span>Tạo danh mục</span>
                            </button>
                            <button
                                onClick={handleCreateNew}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center space-x-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span>Tạo bài viết</span>
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                                title="Đóng"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[300px]">
                            <input
                                type="text"
                                placeholder="Tìm kiếm trong danh mục hiện tại..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex flex-1 flex-col overflow-hidden">
                    {/* Categories Sidebar */}
                    <div className="w-full bg-white border-r border-gray-200 flex flex-col overflow-hidden">
                        {/* Path Navigation */}
                        <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg shadow-md">
                            <button
                                onClick={() => {
                                    setCategoryHistory([]);
                                    setSelectedCategoryUuid('');
                                    if (blogAssetTypeId) {
                                        loadCategoryTree(blogAssetTypeId, 0, '');
                                    }
                                }}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                                Tất cả bài viết
                            </button>

                            {/* Display breadcrumb path from history */}
                            {categoryHistory.map((historyItem, index) => (
                                <React.Fragment key={`breadcrumb-${index}`}>
                                    <span className="text-gray-400">/</span>
                                    <button
                                        onClick={() => {
                                            // Navigate to this level in history
                                            const newHistory = categoryHistory.slice(0, index + 1);
                                            setCategoryHistory(newHistory);
                                            setSelectedCategoryUuid(historyItem.category.uuid);
                                            loadCategoryTree(blogAssetTypeId!, historyItem.level, historyItem.category.uuid);
                                        }}
                                        className="text-blue-600 hover:text-blue-800"
                                        title={historyItem.category.category_title}
                                    >
                                        {historyItem.category.category_title}
                                    </button>
                                </React.Fragment>
                            ))}

                            {categoryHistory.length > 0 && (
                                <button
                                    onClick={handleCategoryBack}
                                    className="ml-auto flex items-center gap-1 px-3 py-1 text-blue-600 hover:text-blue-800"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Quay lại
                                </button>
                            )}
                        </div>

                        {/* Search Results - Categories */}
                        {searchQuery && filteredCategories.length > 0 && (
                            <div className="border-b border-gray-200 bg-blue-50/50">

                                <div className="max-h-48 overflow-y-auto">
                                    {filteredCategories.map((category) => (
                                        <button
                                            key={`search-${category.uuid}`}
                                            onClick={() => handleCategorySelect(category)}
                                            className="w-full text-left px-4 py-2 hover:bg-blue-100/50 transition-colors border-b border-blue-100/50 last:border-b-0"
                                        >
                                            <div className="flex items-center gap-3">
                                                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                                </svg>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {category.category_title}
                                                    </p>
                                                    {category.description && (
                                                        <p className="text-xs text-gray-600 truncate">
                                                            {category.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Category List - Hide when searching */}
                        {!searchQuery && (
                            <div className="flex-1 overflow-y-auto">
                                {/* <div className="divide-y divide-gray-200"> */}
                                {categoryResponse?.categories?.map((category) => (
                                    <button
                                        key={category.uuid}
                                        onClick={() => handleCategorySelect(category)}
                                        className="w-full text-left px-4 py-3 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                            </svg>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {category.category_title}
                                                </p>
                                                {category.description && (
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {category.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingCategory(category);
                                                        setCreateCategoryVisible(true);
                                                    }}
                                                    className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                                    title="Chỉnh sửa danh mục"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteCategory(category);
                                                    }}
                                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                    title="Xóa danh mục"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {/* </div> */}
                            </div>
                        )}
                    </div>

                    {/* Posts Content */}
                    <div className="flex-1 flex flex-col overflow-hidden">

                        {/* Search Results - Posts */}
                        {/* {searchQuery && posts.length > 0 && (
                            <div className="border-b border-gray-200 bg-green-50/50">
                                <div className="px-3 py-2 border-b border-gray-200">
                                    <h3 className="text-sm font-medium text-green-800">
                                        📝 Bài viết ({posts.length})
                                    </h3>
                                </div>
                            </div>
                        )} */}

                        {/* Posts List - Show when not searching or when has search results */}
                        {(!searchQuery || posts.length > 0) && (
                            <div className="flex-1 overflow-y-auto">
                                {isLoading ? (
                                    <div className="flex items-center justify-center h-64">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : posts.length === 0 && selectedCategoryUuid ? (
                                    <div className="flex flex-col h-full items-center justify-center h-64 text-gray-500">
                                        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <p className="text-lg font-medium">Chưa có bài viết nào</p>
                                        <p className="text-sm">Bắt đầu bằng cách tạo bài viết mới</p>
                                    </div>
                                ) : posts.length === 0 ? (
                                    // <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                                    //     <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    //     </svg>
                                    //     <p className="text-lg font-medium">Chưa có bài viết nào</p>
                                    //     <p className="text-sm">Chọn một category để xem bài viết hoặc tạo bài viết mới</p>
                                    // </div>
                                    <>
                                    </>
                                ) : (
                                    <>
                                        {/* <div className="divide-y divide-gray-200"> */}
                                        {posts.map((post) => (
                                            <div
                                                key={post.uuid}
                                                className="px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-200 cursor-pointer"
                                                onClick={() => handleEditPost(post)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {/* Blog Icon */}
                                                    <div className="flex-shrink-0">
                                                        {/* <div className="w-8 h-8 rounded-lg flex items-center justify-center"> */}
                                                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        {/* </div> */}
                                                    </div>

                                                    {/* Featured Image */}
                                                    <div className="flex-shrink-0">
                                                        {post.featuredImage ? (
                                                            <div className="relative w-10 h-10 rounded overflow-hidden">
                                                                <img src={post.featuredImage} alt={post.featuredImage} className="w-full h-full object-cover" />
                                                            </div>
                                                        ) : (
                                                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Content */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                                                    {post.title[currentLanguage?.code || 'vn'] || post.title['vn'] || 'Chưa có tiêu đề'}
                                                                </h4>
                                                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                                                    <span>📅 {formatDate(post.createdAt)} - {post.updater && post.updater?.name ? post.updater.name : post.creator?.name || 'N/A'}</span>
                                                                    <span>👁️ {post.viewCount || 0}</span>
                                                                </div>
                                                            </div>

                                                            {/* Actions */}
                                                            <div className="flex items-center gap-1 ml-3">
                                                                {/* Approve Toggle Button - Only visible for admin */}
                                                                {isAdmin && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleApprovePost(post);
                                                                        }}
                                                                        className={`p-1.5 rounded transition-colors ${post.approve === 1
                                                                            ? 'text-green-600 bg-green-100 hover:bg-green-200'
                                                                            : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                                                                            }`}
                                                                        title={post.approve === 1 ? 'Đã phê duyệt - Click để hủy' : 'Chưa phê duyệt - Click để phê duyệt'}
                                                                    >
                                                                        <svg className="w-4 h-4" fill={post.approve === 1 ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEditPost(post);
                                                                    }}
                                                                    className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                                                    title="Chỉnh sửa bài viết"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeletePost(post);
                                                                    }}
                                                                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                                    title="Xóa bài viết"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {/* </div> */}
                                    </>
                                )}
                            </div>
                        )}

                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {deleteModalVisible && postToDelete && (
                    <div className="fixed inset-0 bg-[#00000099] flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg w-full overflow-hidden max-w-md">
                            {/* Modal Header */}
                            <div className="bg-red-100 px-6 py-4 border-b flex-shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <h2 className="text-xl font-bold text-red-900">
                                        Xác nhận xóa bài viết
                                    </h2>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6">
                                <div className="space-y-4">
                                    <p className="text-gray-700">
                                        Bạn có chắc chắn muốn xóa bài viết này không?
                                    </p>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="font-medium text-gray-900">
                                            {postToDelete.title[currentLanguage?.code || 'vn'] || postToDelete.title['vn'] || 'Chưa có tiêu đề'}
                                        </p>
                                    </div>
                                    <p className="text-sm text-red-600 font-medium">
                                        ⚠️ Hành động này không thể hoàn tác!
                                    </p>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                                <button
                                    onClick={handleCancelDelete}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    onClick={handleConfirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    <span>Xóa bài viết</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Category Confirmation Modal */}
                {deleteCategoryModalVisible && categoryToDelete && (
                    <div className="fixed inset-0 bg-[#00000099] flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg w-full overflow-hidden max-w-md">
                            {/* Modal Header */}
                            <div className="bg-red-100 px-6 py-4 border-b flex-shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <h2 className="text-xl font-bold text-red-900">
                                        Xác nhận xóa danh mục
                                    </h2>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6">
                                <div className="space-y-4">
                                    <p className="text-gray-700">
                                        Bạn có chắc chắn muốn xóa danh mục này không?
                                    </p>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="font-medium text-gray-900 truncate">
                                            {categoryToDelete.category_title}
                                        </p>
                                        {categoryToDelete.description && (
                                            <p className="text-sm text-gray-600 mt-1 truncate">
                                                {categoryToDelete.description}
                                            </p>
                                        )}
                                    </div>
                                    <div className="bg-yellow-50 p-4 rounded-lg">
                                        <p className="text-sm text-yellow-800 font-medium">
                                            ⚠️ Cảnh báo: Xóa danh mục sẽ xóa tất cả bài viết trong danh mục này!
                                        </p>
                                    </div>
                                    <p className="text-sm text-red-600 font-medium">
                                        Hành động này không thể hoàn tác!
                                    </p>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                                <button
                                    onClick={handleCancelDeleteCategory}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    onClick={handleConfirmDeleteCategory}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    <span>Xóa danh mục</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Category Form Modal */}
                {createCategoryVisible && (
                    <div className="fixed inset-0 bg-[#00000099] flex items-center justify-center p-4 rounded-lg z-50">
                        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                            {/* Modal Header */}
                            <div className="bg-gray-100 px-6 py-4 border-b flex-shrink-0">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-bold text-gray-900">
                                        {editingCategory?.uuid ? 'Chỉnh sửa danh mục bài viết' : 'Tạo danh mục bài viết'}
                                    </h2>
                                    <button
                                         onClick={handleCloseCategoryForm}
                                         className="text-gray-500 hover:text-gray-700"
                                     >
                                         <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                         </svg>
                                     </button>
                                 </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 overflow-y-auto flex-1 min-h-0">
                                <form ref={createCategoryFormRef}>
                                    <div className="space-y-6">
                                        {/* Category Titles by Language */}
                                        <div className="space-y-4">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Tên danh mục theo ngôn ngữ
                                            </label>
                                            {languages.map((lang) => {
                                                const isVietnamese = lang.code === 'vn' || lang.id === 'vn';
                                                const inputName = `category_title_${lang.id}`;
                                                return (
                                                    <div key={lang.code || lang.id} className="space-y-2">
                                                        <label className="block text-sm font-medium text-gray-600">
                                                            {lang.name} {isVietnamese && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name={inputName}
                                                            placeholder={`Nhập tên danh mục bằng ${lang.name}`}
                                                            required={isVietnamese}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Description */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Mô tả
                                            </label>
                                            <textarea
                                                name="description"
                                                placeholder="Nhập mô tả (tùy chọn)"
                                                rows={3}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>

                                        {/* Info Box */}
                                        <div className="bg-blue-50 p-4 rounded-lg">
                                            <h3 className="font-medium text-blue-800 mb-2">Thông tin</h3>
                                            <p className="text-sm text-blue-700">
                                                Danh mục này dùng để phân loại các bài viết.
                                            </p>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            {/* Modal Footer */}
                            <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                                <button
                                    onClick={handleCloseCategoryForm}
                                    disabled={createCategoryLoading}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    onClick={handleSaveCategory}
                                    disabled={createCategoryLoading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {createCategoryLoading && (
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    )}
                                    <span>{createCategoryLoading ? 'Đang lưu...' : (editingCategory?.uuid ? 'Lưu thay đổi' : 'Tạo danh mục')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </LayoutDefault>
    );
}
